import { isCustomAuthConfigured } from "./cognitoAuth";
import { authorizeParams, forgetPkce, takeVerifier } from "./pkce";
import { devLog } from '../common/devLog';

const config = window.pcaSettings;

const TOKEN_PREF_KEY = "auth_token_pref";
const RETURN_PATH_KEY = "auth_return_path";
const SIGN_IN_RESTARTED_KEY = "auth_sign_in_restarted";
const SIGNED_OUT_AT_KEY = "auth_signed_out_at";
const CLOCK_SKEW_KEY = "auth_clock_skew";
const LAST_LOGOUT_KEY = "auth_last_logout";

// After a logout, automatic redirects stay on the app's own sign-in card this long instead of
// going to Cognito, which could still sign the user straight back in on its session cookie.
const SIGNED_OUT_QUIET_MS = 60 * 1000;
// Logout waits this long for Cognito to revoke the refresh token, then leaves anyway.
const REVOKE_WAIT_MS = 2000;

// Where Cognito sends the user back to, and where the app's own sign-in card lives.
const appRoot = () => `${window.location.protocol}//${window.location.host}/`;

// Every navigation goes through here.
const navigate = (url) => window.location.assign(url);

function recordLastLogout(reason, err) {
  // The redirect wipes the console, so keep the reason where it can be read after the fact:
  // localStorage.auth_last_logout in the browser console. Never anything secret in it.
  try {
    localStorage.setItem(LAST_LOGOUT_KEY, JSON.stringify({
      at: new Date().toISOString(),
      reason,
      detail: err?.message || null,
    }));
  } catch (_) {
    // storage unavailable: the console line is all there is
  }
}

// What the hosted sign-in sends back on the address; never part of a page to come back to.
const SIGN_IN_RESPONSE = ["code", "state", "error", "error_description"];

function returnPathOf(location) {
  const params = new URLSearchParams(location.search);
  SIGN_IN_RESPONSE.forEach((key) => params.delete(key));
  const search = params.toString();
  return location.pathname + (search ? `?${search}` : "");
}

function saveReturnPath() {
  const path = returnPathOf(window.location);
  if (path && path !== '/') {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
  }
}

// A rejected token (e.g. left over from a different pool/client config, or genuinely revoked)
// must be cleared before going to sign-in. getToken() only checks the exp claim client-side, so
// without this, reloading to the sign-in card re-reads the same bad token, gets rejected again,
// and reloads again in a loop instead of ever reaching the login form.
function clearLocalSession() {
  ["id_token", "access_token", "refresh_token", TOKEN_PREF_KEY, CLOCK_SKEW_KEY]
    .forEach((key) => localStorage.removeItem(key));
  // The SRP form's library (amazon-cognito-identity-js) keeps its own copy of the tokens.
  const libraryPrefix = `CognitoIdentityServiceProvider.${config.auth.clientId}.`;
  const libraryKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(libraryPrefix)) libraryKeys.push(key);
  }
  libraryKeys.forEach((key) => localStorage.removeItem(key));
}

function signedOutRecently() {
  const at = Number(localStorage.getItem(SIGNED_OUT_AT_KEY));
  return at > 0 && Date.now() - at < SIGNED_OUT_QUIET_MS;
}

// The "Sign in" button, and every automatic redirect that ends at Cognito. PKCE and `state` (pkce.js): the
// code Cognito sends back is useless without the verifier this tab kept.
export async function signInWithHostedUi() {
  const params = await authorizeParams({ clientId: config.auth.clientId, redirectUri: appRoot() });
  saveReturnPath();
  devLog("Sign-in redirect:", `${config.auth.uri}/oauth2/authorize`);
  navigate(`${config.auth.uri}/oauth2/authorize?${params}`);
}

let redirecting = null;
let loggingOut = null;

export function redirectToLogin(message, err) {
  // An explicit sign-out is in progress and decides where the browser goes. Following a stray
  // 401 to Cognito now would sign straight back in on its still-valid session cookie.
  if (loggingOut) return undefined;
  if (redirecting) return redirecting;

  console.debug("Redirect to login:", message, err);
  recordLastLogout(typeof message === "string" ? message : "sign-in requested", err);
  saveReturnPath();
  clearLocalSession();

  redirecting = (async () => {
    // With a user pool configured, the sign-in card the app renders at startup (see index.js) is
    // where users belong. Just after a logout it is too: see SIGNED_OUT_QUIET_MS.
    if (isCustomAuthConfigured() || signedOutRecently()) {
      navigate(appRoot());
      return;
    }
    try {
      await signInWithHostedUi();
    } catch (signInError) {
      console.debug("Couldn't start sign-in; showing the sign-in card", signInError);
      navigate(appRoot());
    }
  })();
  return redirecting;
}

export function parseAuthQueryString() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (error) {
    throw new Error(params.get("error_description") || "Invalid configuration");
  }
  return { code: params.get("code"), state: params.get("state") };
}

const SIGN_IN_AGAIN = "Sign-in could not be completed. Please sign in again.";

export async function handleCode(code, state) {
  // Only a code answering this tab's own sign-in request is exchanged, and only with the verifier
  // that request's challenge was made from (pkce.js). Used up either way.
  const verifier = takeVerifier(state);
  // The code is single-use and worthless without the verifier, but keep it out of the address
  // bar and history all the same.
  const url = new URL(window.location.href);
  SIGN_IN_RESPONSE.forEach((key) => url.searchParams.delete(key));

  if (!verifier) {
    window.history.replaceState({}, "", url);
    if (selectAuthToken()) return; // an old reply reloaded over a live session
    if (!sessionStorage.getItem(SIGN_IN_RESTARTED_KEY)) {
      // Not this tab's request (a sign-in started in another tab, an old link, a deploy mid-sign-in,
      // or a code planted by someone else): never exchange it; start a fresh sign-in, once.
      sessionStorage.setItem(SIGN_IN_RESTARTED_KEY, "1");
      recordLastLogout("Sign-in reply did not match this tab's request; restarting sign-in");
      return signInWithHostedUi();
    }
    recordLastLogout("Sign-in reply did not match this tab's request");
    throw new Error(SIGN_IN_AGAIN);
  }

  let data;
  try {
    data = await authRequest("authorization_code", {
      redirect_uri: appRoot(),
      code,
      code_verifier: verifier,
    });
  } catch (err) {
    // Never redirect from here: a failure with a lasting cause would loop through Cognito (its
    // cookie issues a new code, which fails again) forever. index.js shows the sign-in card.
    window.history.replaceState({}, "", url);
    recordLastLogout("Couldn't validate code", err);
    throw new Error(SIGN_IN_AGAIN);
  }

  sessionStorage.removeItem(SIGN_IN_RESTARTED_KEY);
  store(data);
  localStorage.removeItem(SIGNED_OUT_AT_KEY);

  // Restore the page the user was on before the sign-in redirect.
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  if (returnPath) {
    url.pathname = returnPath.split("?")[0];
    url.search = returnPath.includes("?") ? returnPath.slice(returnPath.indexOf("?")) : "";
  }
  window.history.replaceState({}, "", url);
}

async function authRequest(grant_type, data, { timeoutMs } = {}) {
  devLog("Auth request:", grant_type);
  let body = new URLSearchParams();
  body.append("grant_type", grant_type);
  body.append("client_id", config.auth.clientId);
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  const url = `${config.auth.uri}/oauth2/token`;

  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body,
      signal: controller?.signal,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!response.ok) {
    // Cognito says why it refused (e.g. invalid_grant: Refresh Token has expired). Keep that: it is
    // the only clue to why a user was signed out. The body never carries a token.
    let detail = "";
    try {
      const refusal = await response.json();
      detail = [refusal.error, refusal.error_description].filter(Boolean).join(": ");
    } catch (_) {
      // no JSON body
    }
    const error = new Error(`Auth endpoint refused ${grant_type}, HTTP ${response.status}${detail ? `: ${detail}` : ""}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

// Renewal: AWS's guidance is to use a token for about 75% of its lifetime, then get a new one.
// Renewing early also means no request leaves with a token the server already counts as expired.
const RENEW_AT_FRACTION = 0.75;
// For a token without an iat claim: renew this long before it expires.
const RENEW_FALLBACK_SECONDS = 300;
// After Cognito refuses an early renewal, keep using the still-valid token this long before
// asking again, rather than asking on every request.
const REFUSED_RENEWAL_BACKOFF_MS = 60 * 1000;

let renewalPausedUntil = 0;

// Seconds by Cognito's clock: corrected by the skew measured when the tokens were issued, so a PC
// clock running fast doesn't renew (and, with rotation, rotate) on every request.
function nowSeconds() {
  const skew = Number(localStorage.getItem(CLOCK_SKEW_KEY)) || 0;
  return Math.floor(Date.now() / 1000) - skew;
}

function renewalPoint({ iat, exp }) {
  if (Number.isFinite(iat) && iat < exp) {
    return iat + RENEW_AT_FRACTION * (exp - iat);
  }
  return exp - RENEW_FALLBACK_SECONDS;
}

export async function getToken() {
  const token = selectAuthToken();

  if (!token) {
    return;
  }

  let claims = {};
  try {
    claims = payloadFromToken(token);
  } catch (err) {
    console.debug(err);
  }
  const expiresAt = Number(claims.exp) || 0;
  const now = nowSeconds();
  if (now < renewalPoint({ iat: claims.iat, exp: expiresAt })) {
    return token;
  }
  if (now < expiresAt && Date.now() < renewalPausedUntil) {
    return token;
  }

  console.debug(now < expiresAt ? "Token past 75% of its lifetime; renewing" : "Expired token");

  try {
    return await refreshToken();
  } catch (err) {
    const current = selectAuthToken();
    // The session moved on meanwhile: another tab renewed it.
    if (current && current !== token) {
      return current;
    }
    // An early renewal that fails is not a reason to sign out: the token still works for now.
    if (current && nowSeconds() < expiresAt) {
      if (err?.status >= 400 && err?.status < 500) {
        renewalPausedUntil = Date.now() + REFUSED_RENEWAL_BACKOFF_MS;
      }
      console.debug("Early renewal failed; keeping the current token", err);
      return token;
    }
    return redirectToLogin("Error refreshing tokens", err);
  }
}

// Picks the Authorization token matching this stored session's origin, so one
// frontend build works against either authorizer mode. A COGNITO_USER_POOLS
// authorizer with AuthorizationScopes configured (access-token mode, e.g.
// [openid]) only accepts access tokens carrying that scope; one with none
// configured (ID-token mode) only accepts ID tokens. The stored access
// token's own `scope` claim is the discriminator: Hosted-UI/OAuth sessions
// request `openid` (see PCAUserPoolClient.AllowedOAuthScopes in
// cognito.template), while the custom SRP form never goes through OAuth, so
// its access tokens only ever carry the reserved
// aws.cognito.signin.user.admin scope.
// The origin-based default can still mismatch the backend (e.g. a Hosted-UI
// session against a blank-scopes ID-token-mode authorizer). When the API 401s
// the selected token, tryFallbackTokenMode() below records the working
// alternative here so the app adapts to whatever backend is deployed.
const accessTokenHasOpenid = (accessToken) => {
  try {
    const { scope } = payloadFromToken(accessToken);
    return typeof scope === "string" && scope.split(" ").includes("openid");
  } catch (err) {
    console.debug(err);
    return false;
  }
};

function selectAuthToken() {
  const idToken = window.localStorage.getItem("id_token");
  const accessToken = window.localStorage.getItem("access_token");

  const pref = window.localStorage.getItem(TOKEN_PREF_KEY);
  if (pref === "id" && idToken) return idToken;
  if (pref === "access" && accessToken) return accessToken;

  if (accessToken && accessTokenHasOpenid(accessToken)) {
    return accessToken;
  }
  return idToken;
}

// Called by api.js on a 401: switches to the stored session's other token
// type, if one exists that could plausibly be accepted, and returns whether a
// retry is worthwhile. The preference sticks (localStorage) so the switch
// costs one extra round trip per browser, not one per request.
export function tryFallbackTokenMode() {
  const idToken = window.localStorage.getItem("id_token");
  const accessToken = window.localStorage.getItem("access_token");
  const current = selectAuthToken();

  if (current && current === accessToken && idToken) {
    window.localStorage.setItem(TOKEN_PREF_KEY, "id");
    return true;
  }
  if (
    current &&
    current === idToken &&
    accessToken &&
    accessTokenHasOpenid(accessToken) &&
    window.localStorage.getItem(TOKEN_PREF_KEY) !== "access"
  ) {
    window.localStorage.setItem(TOKEN_PREF_KEY, "access");
    return true;
  }
  return false;
}

export function payloadFromToken(token) {
  const parts = token.split(".");

  if (parts.length !== 3) throw new Error("Invalid token");

  // JWTs are base64url, not base64 — atob rejects the '-' and '_' it uses in
  // place of '+' and '/', which a Cognito token's payload segment contains
  // often enough that this was failing on nearly every real token.
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

  return JSON.parse(window.atob(padded));
}

// Concurrent callers (several API calls firing right after app mount) must
// share one in-flight refresh rather than each racing their own — the old
// code cleared the stored tokens before its network call resolved, so a
// second caller reading localStorage mid-refresh saw nothing and got sent
// back through sign-in.
let pendingRefresh = null;

// Tabs share the stored tokens and reach their renewal point together. With refresh token
// rotation, a renewal spends the refresh token, so only one tab may renew at a time.
const REFRESH_LOCK = "pca-auth-refresh";
const REFRESH_TIMEOUT_MS = 10 * 1000;

export async function refreshToken() {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = refreshAcrossTabs().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
}

async function refreshAcrossTabs() {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks?.request) return doRefresh();

  const before = {
    access: localStorage.getItem("access_token"),
    refresh: localStorage.getItem("refresh_token"),
  };
  return locks.request(REFRESH_LOCK, () => {
    // Another tab renewed while this one waited for the lock: its tokens are the session now.
    const access = localStorage.getItem("access_token");
    const refresh = localStorage.getItem("refresh_token");
    if (access && refresh && (access !== before.access || refresh !== before.refresh)) {
      return selectAuthToken();
    }
    return doRefresh();
  });
}

async function doRefresh() {
  const sent = localStorage.getItem("refresh_token");
  if (!sent) {
    throw new Error("No refresh token stored");
  }

  let data;
  try {
    data = await authRequest("refresh_token", { refresh_token: sent }, { timeoutMs: REFRESH_TIMEOUT_MS });
  } catch (err) {
    // Under rotation every renewal replaces the refresh token. If another tab renewed first, the
    // one sent here is spent, and the tokens that tab stored are the session now.
    const current = localStorage.getItem("refresh_token");
    if (current && current !== sent && localStorage.getItem("access_token")) {
      console.debug("Another tab renewed the session; using its tokens");
      return selectAuthToken();
    }
    throw err;
  }

  // Signed out (here or in another tab) while the renewal was in flight: don't bring it back.
  if (!localStorage.getItem("refresh_token")) {
    throw new Error("Signed out while the session was being renewed");
  }

  console.debug("Tokens refreshed");
  store(data);

  // Re-select rather than hardcode: a refreshed session keeps sending
  // whichever token type matches its actual origin (see selectAuthToken).
  return selectAuthToken();
}

function store(data) {
  window.localStorage.setItem("id_token", data.id_token);
  window.localStorage.setItem("access_token", data.access_token);
  // With refresh token rotation each renewal returns a new refresh token, and the old one stops
  // working. Without rotation the response has none: keep the existing one (writing it
  // unconditionally once stored the string "undefined" and broke the next renewal).
  if (data.refresh_token) {
    window.localStorage.setItem("refresh_token", data.refresh_token);
  }
  recordClockSkew(data.access_token);
}

// The token was issued a moment ago, so the gap between this PC's clock and its iat is the clock
// skew (plus a little network time).
function recordClockSkew(accessToken) {
  try {
    const { iat } = payloadFromToken(accessToken);
    if (Number.isFinite(iat)) {
      window.localStorage.setItem(CLOCK_SKEW_KEY, String(Math.round(Date.now() / 1000 - iat)));
      return;
    }
  } catch (_) {
    // not a readable token: no correction
  }
  window.localStorage.removeItem(CLOCK_SKEW_KEY);
}

// Sign-out that ends the session at Cognito too: the refresh token is revoked and Cognito's own
// session cookie cleared, so the next "Sign in" asks for a password (shared call-centre PCs).
export function logOut() {
  if (!loggingOut) loggingOut = doLogOut();
  return loggingOut;
}

async function doLogOut() {
  const refresh = localStorage.getItem("refresh_token");
  recordLastLogout("user logged out");
  // Other tabs see this (watchForSignOutElsewhere) and leave too.
  try {
    localStorage.setItem(SIGNED_OUT_AT_KEY, String(Date.now()));
  } catch (_) {
    // storage unavailable
  }
  clearLocalSession();
  sessionStorage.removeItem(RETURN_PATH_KEY);
  forgetPkce();

  if (refresh) {
    await revokeWithin(refresh, REVOKE_WAIT_MS);
  }

  if (isCustomAuthConfigured()) {
    // The SRP form has no Cognito session cookie to clear.
    navigate(appRoot());
    return;
  }
  const params = new URLSearchParams({ client_id: config.auth.clientId, logout_uri: appRoot() });
  navigate(`${config.auth.uri}/logout?${params}`);
}

// Revoking the refresh token also revokes the tokens issued from it. A slow or failed revocation
// must never keep the user signed in, so this waits at most `ms`; keepalive lets the request
// finish after the page has moved on.
function revokeWithin(token, ms) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  const revoke = (async () => {
    try {
      const response = await fetch(`${config.auth.uri}/oauth2/revoke`, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ token, client_id: config.auth.clientId }),
      });
      if (!response.ok) console.debug("Cognito refused to revoke the refresh token", response.status);
    } catch (err) {
      console.debug("Couldn't revoke the refresh token", err);
    }
  })();
  return Promise.race([revoke, timeout]).finally(() => clearTimeout(timer));
}

// A logout in another tab ends this tab's session too: leave its pages (and any call data on
// screen) for the sign-in card.
export function watchForSignOutElsewhere() {
  const onStorage = (event) => {
    if (event.key === SIGNED_OUT_AT_KEY && event.newValue) {
      navigate(appRoot());
    }
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

export function getAuthHeaders() {
  // Must match getToken(): same per-session token selection.
  const token = selectAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
